import * as i0 from '@angular/core';
import { InjectionToken, Injectable, APP_INITIALIZER, PLATFORM_ID, Injector, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

const DEFAULT_COUNTER_ID_AOT = new InjectionToken('DEFAULT_COUNTER_ID_AOT');
const DEFAULT_COUNTER_ID = new InjectionToken('DEFAULT_COUNTER_ID');
const YANDEX_COUNTERS_CONFIGS_AOT = new InjectionToken('YANDEX_COUNTERS_CONFIGS_AOT');
const YANDEX_COUNTERS_CONFIGS = new InjectionToken('YANDEX_COUNTERS_CONFIGS');
class YandexCounterConfig {
    constructor() {
        this.clickmap = true;
        this.trackLinks = true;
        this.accurateTrackBounce = true;
        this.trackPageViews = true;
        this.webvisor = false;
        this.trackHash = true;
        this.ut = 'noindex';
    }
}

class Metrika {
    constructor(injector, router) {
        this.defaultCounterId = injector.get(DEFAULT_COUNTER_ID);
        this.counterConfigs = injector.get(YANDEX_COUNTERS_CONFIGS);
        this.positionToId = this.counterConfigs.map(config => config.id);
        const defaultCounterConfig = this.counterConfigs.find(({ id }) => id === this.defaultCounterId || !this.defaultCounterId);
        if (router && defaultCounterConfig?.trackPageViews) {
            router.events.pipe(filter(event => event instanceof NavigationEnd), tap(() => {
                const newUrl = router.url;
                if (!this.previousUrl) {
                    setTimeout(() => {
                        this.previousUrl = newUrl;
                        this.hit(newUrl, { referer: this.previousUrl });
                    }, 100);
                }
                else if (this.previousUrl !== newUrl) {
                    this.previousUrl = newUrl;
                    this.hit(newUrl, { referer: this.previousUrl });
                }
            })).subscribe();
        }
    }
    static getCounterNameById(id) {
        return `yaCounter${id}`;
    }
    static getCounterById(id) {
        return window[Metrika.getCounterNameById(id)];
    }
    async addFileExtension(extensions, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            counter.addFileExtension(extensions);
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    async extLink(url, options = {}, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            const promise = this.getCallbackPromise(options, url);
            counter.extLink(url, options);
            return promise;
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    async file(url, options = {}, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            const promise = this.getCallbackPromise(options, url);
            counter.file(url, options);
            return promise;
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    getClientID(counterPosition) {
        const counter = this.getCounterByPosition(counterPosition);
        if (counter && counter.reachGoal) {
            return counter.getClientID();
        }
        console.warn('Counter is still loading');
    }
    async setUserID(userId, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            counter.setUserID(userId);
            return { userId, counterPosition };
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    async userParams(params, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            counter.userParams(params);
            return { params, counterPosition };
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    async params(params, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            counter.params(params);
            return { params, counterPosition };
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    async replacePhones(counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            counter.replacePhones();
            return { counterPosition };
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    async notBounce(options = {}, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            const promise = this.getCallbackPromise(options, options);
            counter.notBounce(options);
            return promise;
        }
        catch (error) {
            console.warn('Counter is still loading');
        }
    }
    async fireEvent(type, options = {}, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            const promise = this.getCallbackPromise(options, options);
            counter.reachGoal(type, options.params, options.callback, options.ctx);
            return promise;
        }
        catch (error) {
            console.warn(`Event with type [${type}] can\'t be fired because counter is still loading`);
        }
    }
    async hit(url, options = {}, counterPosition) {
        try {
            const counter = await this.counterIsLoaded(counterPosition);
            const promise = this.getCallbackPromise(options, options);
            counter.hit(url, options);
            return promise;
        }
        catch (error) {
            console.warn(`Hit for page [${url}] can\'t be fired because counter is still loading`);
        }
    }
    getCallbackPromise(options, resolveWith) {
        return new Promise((resolve, reject) => {
            const optionsCallback = options.callback;
            options.callback = function () {
                if (optionsCallback) {
                    optionsCallback.call(this);
                }
                resolve(resolveWith);
            };
        });
    }
    counterIsLoaded(counterPosition) {
        const counter = this.getCounterByPosition(counterPosition);
        if (counter && counter.reachGoal) {
            return Promise.resolve(counter);
        }
        else {
            return Promise.reject(new Error('Counter is not loaded'));
        }
    }
    getCounterByPosition(counterPosition) {
        const counterId = this.getCounterIdByPosition(counterPosition);
        return Metrika.getCounterById(counterId);
    }
    getCounterIdByPosition(counterPosition) {
        return (counterPosition === undefined)
            ? this.defaultCounterId
            : this.positionToId[counterPosition];
    }
}
/** @nocollapse */ /** @nocollapse */ Metrika.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: Metrika, deps: [{ token: i0.Injector }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ /** @nocollapse */ Metrika.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: Metrika, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: Metrika, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i1.Router }]; } });

function defaultCounterIdFactory(counterConfigs, defaultCounter) {
    return defineDefaultId(counterConfigs, defaultCounter);
}
function defineDefaultId(counterConfigs, defaultCounter) {
    let configs;
    if (counterConfigs instanceof Array) {
        configs = counterConfigs;
    }
    else {
        configs = [counterConfigs];
    }
    let defaultId;
    if (!defaultCounter) {
        defaultId = configs[0].id;
    }
    else if (typeof defaultCounter === 'number' && defaultCounter < configs.length) {
        defaultId = configs[defaultCounter].id;
    }
    else {
        defaultId = defaultCounter;
    }
    if (!defaultId) {
        console.warn('You provided wrong counter id as a default:', defaultCounter);
        return;
    }
    let defaultCounterExists = false;
    let config;
    for (let i = 0; i < configs.length; i++) {
        config = configs[i];
        if (!config.id) {
            console.warn('You should provide counter id to use Yandex metrika counter', config);
            continue;
        }
        if (config.id === defaultId) {
            defaultCounterExists = true;
        }
    }
    if (!defaultCounterExists) {
        console.warn('You provided wrong counter id as a default:', defaultCounter);
    }
    return defaultId;
}
function countersFactory(configs) {
    return createConfigs(configs);
}
function createConfigs(configs) {
    let counterConfigs;
    if (configs instanceof Array) {
        counterConfigs = configs;
    }
    else {
        counterConfigs = [configs];
    }
    return counterConfigs.map((config) => Object.assign(new YandexCounterConfig(), config));
}
function appInitializerFactory(counterConfigs, platformId) {
    if (isPlatformBrowser(platformId)) {
        return insertMetrika.bind(null, counterConfigs);
    }
    return () => 'none';
}
function insertMetrika(counterConfigs) {
    const name = 'yandex_metrika_callbacks2';
    window[name] = window[name] || [];
    window[name].push(() => {
        try {
            for (const config of counterConfigs) {
                createCounter(config);
            }
        }
        catch (e) { }
    });
    const n = document.getElementsByTagName('script')[0];
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    const alternative = counterConfigs.find(config => config.alternative);
    if (alternative) {
        s.src = 'https://cdn.jsdelivr.net/npm/yandex-metrica-watch/tag.js';
    }
    else {
        s.src = 'https://mc.yandex.ru/metrika/tag.js';
    }
    const insetScriptTag = () => n.parentNode.insertBefore(s, n);
    if (window.opera === '[object Opera]') {
        document.addEventListener('DOMContentLoaded', insetScriptTag, false);
    }
    else {
        insetScriptTag();
    }
    return name;
}
function createCounter(config) {
    window[getCounterNameById(config.id)] = new Ya.Metrika2(config);
}
function getCounterNameById(id) {
    return `yaCounter${id}`;
}

class MetrikaModule {
    static forRoot(configs, defaultCounterId) {
        return {
            ngModule: MetrikaModule,
            providers: [
                {
                    provide: DEFAULT_COUNTER_ID_AOT,
                    useValue: defaultCounterId,
                },
                {
                    provide: YANDEX_COUNTERS_CONFIGS_AOT,
                    useValue: configs,
                },
                {
                    provide: DEFAULT_COUNTER_ID,
                    useFactory: defaultCounterIdFactory,
                    deps: [YANDEX_COUNTERS_CONFIGS_AOT, DEFAULT_COUNTER_ID_AOT],
                },
                {
                    provide: YANDEX_COUNTERS_CONFIGS,
                    useFactory: countersFactory,
                    deps: [YANDEX_COUNTERS_CONFIGS_AOT],
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: appInitializerFactory,
                    deps: [YANDEX_COUNTERS_CONFIGS, PLATFORM_ID],
                    multi: true,
                },
                {
                    provide: Metrika,
                    useClass: Metrika,
                    deps: [Injector, Router],
                },
            ],
        };
    }
}
/** @nocollapse */ /** @nocollapse */ MetrikaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: MetrikaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ /** @nocollapse */ MetrikaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: MetrikaModule });
/** @nocollapse */ /** @nocollapse */ MetrikaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: MetrikaModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: MetrikaModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Metrika, MetrikaModule, appInitializerFactory, countersFactory, createConfigs, createCounter, defaultCounterIdFactory, defineDefaultId, getCounterNameById, insertMetrika };
